import Image from "next/image"
import { useSnapCarousel } from "@/hooks/use-snap-carousel"
import { cn } from "cn"

import { CF_S3_URL } from "@/lib/cloudflare-loader"
import { AriaButton } from "../ui/aria-button"

export interface BusinessTestimonial {
  name: string
  title: string
  company: string
  quote: string
  bg: string
  image: string
  type: "destination" | "hotel"
}

const TESTIMONIALS: BusinessTestimonial[] = [
  {
    company: "Brand USA",
    name: "Janette Roush",
    title: "SVP, Innovation and Chief AI Officer",
    quote:
      "AI trip planning represents a pivotal shift in how we connect with travelers — meeting them where they are with the personalized, intuitive support they now expect. We are thrilled to partner with Mindtrip and bring this to the masses in ways that showcase the rich diversity of U.S. destinations, making them more accessible, inspiring, and aligned with how people actually plan their journeys today.",
    bg: "#EEFFFA",
    image: "Testi_BrandUSA.png",
    type: "destination",
  },
  {
    company: "See Monterey",
    name: "Leslie Chavez",
    title: "Marketing Director",
    quote:
      "At See Monterey, we are always exploring innovative solutions to elevate the visitor experience. Mindtrip stood out as one of the few vendors with real-world experience working with DMOs. During our evaluation, we were impressed by their platform’s potential to transform trip planning through interactive maps, in-depth reviews, customizable itineraries, and seamless tools for sharing and collaboration. These features are a game-changer, giving us new ways to inspire visitors to spend more time exploring Monterey’s unmatched beauty.",
    bg: "#FDFBE8",
    image: "Testi_SeeMont.png",
    type: "destination",
  },
  {
    company: "New Orleans & Company",
    name: "Walt Leger III",
    title: "President and CEO",
    quote:
      "With the addition of Mindtrip for Business, we are bringing our rich content from our award-winning website neworleans.com to life so that everyone can more easily discover and plan an unforgettable trip to New Orleans, to support our incredible culture bearers and the countless small businesses that are the backbone of our community.",
    bg: "#ECF6D6",
    image: "Testi_NewOrleans.png",
    type: "destination",
  },
  {
    company: "Outer Banks Visitors Bureau",
    name: "Lee Nettles",
    title: "Executive Director",
    quote:
      "I wanted to be at the forefront of AI innovation because it will fundamentally transform travel planning and purchasing forever. When I discovered what Mindtrip was doing, I knew it was the direction we needed to pursue. My interaction with the team has been refreshing – they really listen to my feedback. I don’t feel like I was pushed into a product that is one size fits all. It’s constantly evolving.",
    bg: "#F5F0FE",
    image: "Testi_OB.png",
    type: "destination",
  },
  {
    company: "The Landsby",
    name: "Leslie Inlow",
    title: "Director of Marketing",
    quote:
      "As a small hospitality team, we needed an AI partner that was both powerful and practical. Mindtrip stood out as the most intuitive platform we tested, and getting it live only took a few weeks. It goes far beyond a chatbot, bringing concierge-level trip planning to our website and easing front-desk volume. As we look toward the future of hospitality, this is exactly the partner we want by our side.",
    bg: "#E2FCFF",
    image: "Testi_Lands.png",
    type: "hotel",
  },
]

export function BusinessTestimonials({
  type,
}: {
  type: "destination" | "hotel"
}) {
  const { scrollRef, activePageIndex, goTo } = useSnapCarousel()

  const testimonialsToShow = TESTIMONIALS.filter(
    (testimonial) => testimonial.type === type
  )

  return (
    <div className="mx-auto max-w-310 pt-24">
      <div
        className={cn(
          "items-center gap-16 from-[#C4FDDA] to-[#C4F5FD] text-white lg:flex lg:rounded-7xl lg:bg-linear-to-r lg:px-12 lg:text-black xl:px-30",
          type === "hotel"
            ? "from-[#E0F7FF] to-[#ACD7FF]"
            : "from-[#C4FDDA] to-[#C4F5FD]"
        )}
      >
        <div
          className={cn(
            "shrink-0 basis-68 px-9 pb-8 text-center lg:px-0 lg:py-20 lg:text-left",
            testimonialsToShow.length > 1 && "lg:pb-30"
          )}
        >
          <h2 className="mb-9 text-5xl font-semibold">In Their Own Words</h2>
          <p className="text-lg text-pretty">
            {type === "hotel"
              ? "What today’s hoteliers want from AI — and how Mindtrip delivers."
              : "What today’s travel brands want from AI — and how Mindtrip delivers."}
          </p>
        </div>
        <div
          className={cn(
            "relative flex-1 overflow-hidden text-black lg:py-20",
            testimonialsToShow.length > 1 && "lg:pb-30"
          )}
        >
          <div className="flex overflow-hidden rounded-4xl">
            <div
              ref={scrollRef}
              className="scrollbar-hidden flex flex-1 snap-x snap-mandatory overflow-x-auto"
            >
              {testimonialsToShow.map((testimonial) => (
                <div
                  key={testimonial.name}
                  className="flex flex-1 shrink-0 basis-full snap-start flex-col px-4 py-7 sm:px-14"
                  style={{ backgroundColor: testimonial.bg }}
                >
                  <div className="font-serif text-8xl font-bold">“</div>
                  <h3 className="-mt-5 mb-1.5 font-semibold">
                    {testimonial.company}
                  </h3>
                  <div className="mx-auto mb-6 h-0.5 w-9 rounded-full bg-current" />
                  <p className="my-auto font-serif text-pretty italic sm:text-xl">
                    {testimonial.quote}
                  </p>
                  <div className="mx-auto flex items-center gap-2.5 px-4 pt-6 sm:w-fit sm:gap-5 sm:px-0">
                    <Image
                      src={`${CF_S3_URL}/web/${testimonial.image}`}
                      alt=""
                      width={72}
                      height={72}
                      unoptimized
                      className="shrink-0 rounded-full shadow-md max-sm:size-16"
                    />
                    <div className="text-left">
                      <div className="text-base font-semibold sm:text-base">
                        {testimonial.name}
                      </div>
                      <div className="text-xs text-pretty text-gray-10 italic sm:text-sm">
                        {testimonial.title}, {testimonial.company}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {testimonialsToShow.length > 1 && (
            <div className="inset-x-0 bottom-16 mt-8 flex justify-center gap-1.5 lg:absolute lg:mt-0 lg:gap-4">
              {testimonialsToShow.map((_, index) => (
                <AriaButton
                  key={index}
                  className={cn(
                    "size-1.5 rounded-full bg-white lg:size-4 lg:bg-black",
                    index !== activePageIndex &&
                      "opacity-40 transition-opacity lg:opacity-20 lg:hover:opacity-30"
                  )}
                  onClick={() => goTo(index)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
